import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BusinessPartnerProviderConfig,
  Provider,
  ProviderConnectionTestResult,
  TestBusinessPartnerProviderPayload,
  UpsertBusinessPartnerProviderPayload
} from '../../../model/provider';
import { CopilotApiService } from '../../../services/copilot-api-service';

@Component({
  selector: 'app-provider-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './provider-dialog.html',
  styleUrl: './provider-dialog.css'
})
export class ProviderDialog implements OnChanges {
  @Input() submitting: boolean = false;
  @Input() mode: 'add' | 'edit' = 'add';
  @Input() providerTypes: Provider[] = [];
  @Input() providerTypesLoading: boolean = false;
  @Input() initialProvider: BusinessPartnerProviderConfig | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() submitProvider = new EventEmitter<UpsertBusinessPartnerProviderPayload>();

  protected open: boolean = false;
  protected searchTerm: string = '';
  protected providerId: number | null = null;
  protected url: string = '';
  protected apiKey: string = '';
  protected username: string = '';
  protected password: string = '';
  protected error: string | null = null;
  protected testing: boolean = false;
  protected testResult: ProviderConnectionTestResult | null = null;

  constructor(
    private readonly translate: TranslateService,
    private readonly api: CopilotApiService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialProvider']) {
      this.populateForm();
    }
  }

  protected onCancel(): void {
    this.closed.emit();
  }

  protected toggleOpen(): void {
    if (this.isDisabled) {
      this.open = false;
      return;
    }
    this.open = !this.open;
  }

  protected onSearchChange(value: string): void {
    this.searchTerm = value;
  }

  protected clearSearch(): void {
    this.searchTerm = '';
  }

  protected selectProviderType(providerType: Provider): void {
    this.providerId = providerType.Id;
    this.testResult = null;
    this.open = false;
    this.clearSearch();
  }

  protected onSubmit(): void {
    const providerId = this.readProviderId();
    const url = this.url.trim();

    if (providerId == null) {
      this.error = this.translate.instant('providers.validation_provider_type');
      return;
    }

    if (!this.isAbsoluteHttpUrl(url)) {
      this.error = this.translate.instant('providers.validation_url');
      return;
    }

    this.error = null;
    this.submitProvider.emit({
      ProviderId: providerId,
      Url: url,
      ApiKey: this.normalizeOptionalString(this.apiKey),
      Username: this.normalizeOptionalString(this.username),
      Password: this.normalizeOptionalString(this.password)
    });
  }

  protected onTestConnection(): void {
    const providerId = this.readProviderId();
    const url = this.url.trim();

    if (providerId == null) {
      this.error = this.translate.instant('providers.validation_provider_type');
      return;
    }
    if (!this.isAbsoluteHttpUrl(url)) {
      this.error = this.translate.instant('providers.validation_url');
      return;
    }

    this.error = null;
    this.testResult = null;
    this.testing = true;

    const payload: TestBusinessPartnerProviderPayload = {
      ProviderId: providerId,
      Url: url,
      Username: this.normalizeOptionalString(this.username),
      Password: this.normalizeOptionalString(this.password)
    };
    if (this.initialProvider != null) {
      payload.Id = this.initialProvider.Id;
    }

    this.api.testBusinessPartnerProvider(payload).subscribe({
      next: (result) => {
        this.testResult = result;
        this.testing = false;
      },
      error: () => {
        this.testResult = { ok: false, reason: 'unreachable' };
        this.testing = false;
      }
    });
  }

  protected onCredentialFieldChange(): void {
    this.testResult = null;
  }

  private populateForm(): void {
    const provider = this.initialProvider;
    this.testResult = null;
    this.testing = false;
    if (provider == null) {
      this.providerId = null;
      this.url = '';
      this.apiKey = '';
      this.username = '';
      this.password = '';
      this.error = null;
      this.searchTerm = '';
      this.open = false;
      return;
    }

    this.providerId = provider.ProviderId;
    this.url = provider.Url || '';
    this.apiKey = provider.ApiKey || '';
    this.username = provider.Username || '';
    this.password = '';
    this.error = null;
    this.searchTerm = '';
    this.open = false;
  }

  private readProviderId(): number | null {
    if (!Number.isFinite(Number(this.providerId)) || Number(this.providerId) <= 0) {
      return null;
    }
    return Number(this.providerId);
  }

  private isAbsoluteHttpUrl(value: string): boolean {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private normalizeOptionalString(value: string): string | null {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  protected get selectedProviderType(): Provider | null {
    return this.providerTypes.find((providerType) => providerType.Id === this.providerId) ?? null;
  }

  protected get filteredProviderTypes(): Provider[] {
    const query = this.searchTerm.trim().toLowerCase();
    if (!query) {
      return this.providerTypes;
    }

    return this.providerTypes.filter((providerType) => String(providerType.Name || '').toLowerCase().includes(query));
  }

  protected get isDisabled(): boolean {
    return this.submitting || this.providerTypesLoading;
  }

  protected get isAzugaSelected(): boolean {
    return String(this.selectedProviderType?.Name || '').toLowerCase().includes('azuga');
  }

  protected get testResultMessageKey(): string | null {
    if (this.testResult == null) {
      return null;
    }
    if (this.testResult.ok) {
      return 'providers.test_success';
    }
    return this.testResult.reason === 'invalid_credentials'
      ? 'providers.test_failed_credentials'
      : 'providers.test_failed_unreachable';
  }

  @HostListener('document:click')
  protected onDocumentClick(): void {
    this.open = false;
  }
}
